/**
 * Admin V3 — Partners — Search / filter bar
 *
 * Layout (left → right):
 *   [Status filter] [Type filter] [Search input] [Result counter]
 *
 * Both dropdowns server-side; search is client-side over the loaded page
 * (name + contact_email).
 *
 * NO CSV (no backend endpoint).
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { partnerStatusMeta, partnerTypeMeta } from '../../shared/visualTokens';
import { t } from '../../i18n';

const ALL_SENTINEL = '__all__';

const STATUS_VALUES = ['active', 'suspended', 'pending'];
const TYPE_VALUES = ['hotel', 'corporate', 'reseller'];

const PartnersListSearchBar = ({
  status,
  onStatusChange,
  type,
  onTypeChange,
  search,
  onSearchChange,
  resultsCount,
  isFilterActive,
  loading,
  locale = 'fr',
}) => {
  const statusSelectValue = status || ALL_SENTINEL;
  const typeSelectValue = type || ALL_SENTINEL;
  const handleStatus = (v) => onStatusChange(v === ALL_SENTINEL ? '' : v);
  const handleType = (v) => onTypeChange(v === ALL_SENTINEL ? '' : v);

  const resultsCountText = t('partners.results.count', locale).replace('{n}', String(resultsCount));

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Select value={statusSelectValue} onValueChange={handleStatus}>
        <SelectTrigger
          className="h-10 w-[180px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-partners-status-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SENTINEL}>{t('partners.filter.statusAll', locale)}</SelectItem>
          {STATUS_VALUES.map((v) => (
            <SelectItem key={v} value={v}>{partnerStatusMeta[v]?.label || v}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={typeSelectValue} onValueChange={handleType}>
        <SelectTrigger
          className="h-10 w-[180px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-partners-type-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SENTINEL}>{t('partners.filter.typeAll', locale)}</SelectItem>
          {TYPE_VALUES.map((v) => (
            <SelectItem key={v} value={v}>{partnerTypeMeta[v]?.label || v}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="relative flex-1 max-w-md min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('partners.search.placeholder', locale)}
          data-testid="adminv3-partners-search"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="adminv3-partners-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isFilterActive && !loading && (
        <span className="text-sm text-slate-400" data-testid="adminv3-partners-results-count">
          {resultsCountText}
        </span>
      )}
    </div>
  );
};

export default PartnersListSearchBar;
